/**
 * Library Page
 *
 * Shows user's saved music: Liked Songs, playlists, saved albums/artists.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLikesStore } from '@/stores/likes';
import { usePlayerStore } from '@/stores/player';
import { PageHeader } from '@/components/common/PageHeader';
import { HeartIcon, PlayIcon, ShuffleIcon } from '@/components/icons';

export function LibraryPage() {
  const { likedSongs } = useLikesStore();
  const { play } = usePlayerStore();

  const handlePlayLiked = () => {
    if (likedSongs.length > 0) {
      const songs = likedSongs.map((s) => ({
        id: s.id,
        title: s.title,
        artists: s.artists,
        thumbnail: s.thumbnail,
        thumbnails: s.thumbnails,
      }));
      play(songs[0] as any, songs as any[]);
    }
  };

  const handleShuffleLiked = () => {
    if (likedSongs.length > 0) {
      const songs = likedSongs.map((s) => ({
        id: s.id,
        title: s.title,
        artists: s.artists,
        thumbnail: s.thumbnail,
        thumbnails: s.thumbnails,
      }));
      const shuffled = [...songs].sort(() => Math.random() - 0.5);
      play(shuffled[0] as any, shuffled as any[]);
    }
  };

  return (
    <div className="min-h-full">
      <PageHeader title="Library" />

      <div className="px-4 pb-8 space-y-6">
        {/* Liked Songs Card */}
        <Link
          to="/liked"
          className="block bg-gradient-to-br from-primary/30 to-primary/10 rounded-2xl p-4 hover:from-primary/40 hover:to-primary/20 transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-primary-700 flex items-center justify-center shadow-lg">
              <HeartIcon className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold">Liked Songs</h2>
              <p className="text-sm text-on-surface-variant">
                {likedSongs.length} {likedSongs.length === 1 ? 'song' : 'songs'}
              </p>
            </div>
            {likedSongs.length > 0 && (
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleShuffleLiked();
                  }}
                  className="btn-icon bg-surface-container-high"
                >
                  <ShuffleIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handlePlayLiked();
                  }}
                  className="w-10 h-10 rounded-full bg-primary flex items-center justify-center"
                >
                  <PlayIcon className="w-5 h-5 text-white ml-0.5" />
                </button>
              </div>
            )}
          </div>
        </Link>

        {/* Recent Likes Preview */}
        {likedSongs.length > 0 && (
          <section>
            <h3 className="text-lg font-bold mb-3">Recently Liked</h3>
            <div className="space-y-1">
              {likedSongs.slice(0, 5).map((song, index) => {
                const thumbnail = song.thumbnail || song.thumbnails?.[0]?.url || './pwa-192x192.webp';
                return (
                  <button
                    key={song.id}
                    onClick={() => {
                      const songs = likedSongs.map((s) => ({
                        id: s.id,
                        title: s.title,
                        artists: s.artists,
                        thumbnail: s.thumbnail,
                        thumbnails: s.thumbnails,
                      }));
                      play(songs[index] as any, songs as any[]);
                    }}
                    className="w-full flex items-center gap-3 py-2 px-1 hover:bg-surface-container/50 transition-colors rounded-lg"
                  >
                    <img
                      src={thumbnail}
                      alt={song.title}
                      className="w-12 h-12 rounded-lg object-cover"
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = './pwa-192x192.webp';
                      }}
                    />
                    <div className="flex-1 text-left min-w-0">
                      <p className="font-medium line-clamp-1">{song.title}</p>
                      <p className="text-sm text-on-surface-variant line-clamp-1">
                        {song.artists?.map((a) => a.name).join(', ')}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
            {likedSongs.length > 5 && (
              <Link
                to="/liked"
                className="block text-center text-sm text-primary mt-3 hover:underline"
              >
                See all {likedSongs.length} liked songs
              </Link>
            )}
          </section>
        )}

        {/* Empty state */}
        {likedSongs.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-surface-container flex items-center justify-center">
              <HeartIcon className="w-8 h-8 text-on-surface-variant" />
            </div>
            <p className="text-on-surface-variant mb-2">No liked songs yet</p>
            <p className="text-sm text-on-surface-variant/60">
              Tap the heart icon on any song to save it here
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
